import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { apiClient, ApiError } from '../lib/api-client';
import { useAuth } from '../lib/auth-context';

/**
 * F1-018 — Tela de ativacao de MFA TOTP.
 *
 * Shape esperado do backend:
 *   GET  /auth/mfa/setup   -> { secret, otpauth_uri, qr_svg }
 *     - `secret` pode ser exibido (manual key) mas JAMAIS logado
 *     - `qr_svg` vem como string SVG ja renderizado no backend (provisional
 *       enquanto qrcode.react nao for adicionado — decisao enfileirada)
 *   POST /auth/mfa/enable  body { code } -> { recovery_codes: string[] }
 *
 * Importante: nao acessamos o segredo diretamente em codigo — apenas
 * renderizamos o QR fornecido pelo backend (SVG inline controlado).
 */

interface MfaSetupData {
  readonly secret: string;
  readonly otpauth_uri: string;
  readonly qr_svg: string;
}

interface MfaEnableResponse {
  readonly recovery_codes: readonly string[];
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; setup: MfaSetupData }
  | { kind: 'error'; message: string }
  | {
      kind: 'confirmed';
      setup: MfaSetupData;
      recoveryCodes: readonly string[];
    };

function SafeSvg({ svg }: { svg: string }): JSX.Element {
  // Renderizacao de SVG controlado do backend. Nao aceitamos SVG de usuario.
  return (
    <div
      className="mfa-qr"
      role="img"
      aria-label={t('mfa.setup.stepScan')}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export function MfaSetupPage(): JSX.Element {
  const navigate = useNavigate();
  const { setMfaVerified } = useAuth();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedRecovery, setCopiedRecovery] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<MfaSetupData>('/auth/mfa/setup');
        if (cancelled) return;
        setPhase({ kind: 'ready', setup: data });
      } catch {
        if (cancelled) return;
        setPhase({ kind: 'error', message: t('mfa.setup.loadFailed') });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const doConfirm = async (): Promise<void> => {
    if (phase.kind !== 'ready') return;
    setSubmitError(null);
    if (!/^\d{6}$/.test(code)) {
      setSubmitError(t('mfa.setup.invalidCode'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post<MfaEnableResponse>('/auth/mfa/enable', {
        code,
      });
      setMfaVerified();
      setPhase({
        kind: 'confirmed',
        setup: phase.setup,
        recoveryCodes: res.recovery_codes,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setSubmitError(t('mfa.setup.invalidCode'));
      } else {
        setSubmitError(t('mfa.setup.loadFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async (value: string): Promise<boolean> => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard !== undefined) {
        await navigator.clipboard.writeText(value);
        return true;
      }
    } catch {
      // fallback silencioso
    }
    return false;
  };

  return (
    <main className="auth-page" role="main">
      <section className="auth-card mfa-setup" aria-labelledby="mfa-setup-title">
        <h1 id="mfa-setup-title">{t('mfa.setup.title')}</h1>
        <p>{t('mfa.setup.description')}</p>

        {phase.kind === 'loading' ? <p role="status">{t('common.loading')}</p> : null}

        {phase.kind === 'error' ? (
          <p role="alert" className="error">
            {phase.message}
          </p>
        ) : null}

        {phase.kind === 'ready' ? (
          <>
            <h2>{t('mfa.setup.stepScan')}</h2>
            <SafeSvg svg={phase.setup.qr_svg} />

            <details className="mfa-manual">
              <summary>{t('mfa.setup.manualKey')}</summary>
              <code className="mfa-secret" aria-label={t('mfa.setup.manualKey')}>
                {phase.setup.secret}
              </code>
              <button
                type="button"
                onClick={() => {
                  void copyToClipboard(phase.setup.secret).then((ok) => {
                    if (ok) setCopiedKey(true);
                  });
                }}
              >
                {copiedKey ? t('mfa.setup.copied') : t('mfa.setup.copyKey')}
              </button>
            </details>

            <form
              onSubmit={(ev) => {
                ev.preventDefault();
                void doConfirm();
              }}
              noValidate
            >
              <h2>{t('mfa.setup.stepCode')}</h2>
              <label htmlFor="mfa-code">{t('mfa.setup.codeLabel')}</label>
              <input
                id="mfa-code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern="[0-9]{6}"
                required
                value={code}
                placeholder={t('mfa.setup.codePlaceholder')}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />

              {submitError !== null ? (
                <p role="alert" className="error">
                  {submitError}
                </p>
              ) : null}

              <button type="submit" disabled={submitting}>
                {submitting ? t('mfa.setup.submitting') : t('mfa.setup.confirm')}
              </button>
            </form>
          </>
        ) : null}

        {phase.kind === 'confirmed' ? (
          <section className="mfa-recovery" aria-labelledby="mfa-recovery-title">
            <h2 id="mfa-recovery-title">{t('mfa.setup.recoveryTitle')}</h2>
            <p>{t('mfa.setup.recoverySubtitle')}</p>
            <ul className="recovery-codes" aria-label={t('mfa.setup.recoveryTitle')}>
              {phase.recoveryCodes.map((rc) => (
                <li key={rc}>
                  <code>{rc}</code>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => {
                void copyToClipboard(phase.recoveryCodes.join('\n')).then((ok) => {
                  if (ok) setCopiedRecovery(true);
                });
              }}
            >
              {copiedRecovery ? t('mfa.setup.copied') : t('mfa.setup.copyRecovery')}
            </button>
            <button type="button" onClick={() => navigate('/dashboard', { replace: true })}>
              {t('mfa.setup.done')}
            </button>
          </section>
        ) : null}
      </section>
    </main>
  );
}
